import { useEffect, useRef } from "react";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";

const workerSrc = "/scripts/pdf.worker.min.js";
GlobalWorkerOptions.workerSrc = workerSrc;

const THUMBNAIL_WIDTH = 300;

function PdfThumbnail({
  fileUrl,
  width = THUMBNAIL_WIDTH
}: Readonly<{
  fileUrl: string;
  width?: number;
}>) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    let cancelled = false;

    const renderThumbnail = async () => {
      const loadingTask = getDocument({ url: fileUrl });
      const pdfDoc = await loadingTask.promise;

      if (cancelled) {
        pdfDoc.destroy();
        return;
      }

      const page = await pdfDoc.getPage(1);
      const viewport = page.getViewport({ scale: 1 });
      const scale = width / viewport.width;
      const scaledViewport = page.getViewport({ scale });

      const canvas = canvasRef.current;
      if (!canvas || cancelled) {
        pdfDoc.destroy();
        return;
      }

      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;
      const context = canvas.getContext("2d");
      if (!context) {
        pdfDoc.destroy();
        return;
      }

      await page.render({ canvasContext: context, viewport: scaledViewport })
        .promise;

      if (cancelled) {
        pdfDoc.destroy();
      }
    };

    renderThumbnail().catch(() => {
      // Silently handle errors for thumbnail rendering
    });

    return () => {
      cancelled = true;
    };
  }, [fileUrl, width]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="pdf-thumbnail-canvas"
      style={{ maxWidth: "100%", display: "block" }}
    />
  );
}

export default PdfThumbnail;
